import {
  type LDClientInterface,
  LDClientWrapper,
  type LDSingleKindContext,
  screenSize,
} from "@hoylu/client-common";
import type { Store } from "redux";
import { from } from "rxjs";
import type { RootState } from "typesafe-actions";
import { version } from "../package.json";
import { updateFlags } from "./state/config/config.actions";
import type { ConfigState, FeatureFlags } from "./state/config/config.reducer";
import type { User } from "./state/user/user.reducer";

function getPlatform(config: ConfigState) {
  if (config.teamsClient) {
    return "teams";
  }
  if (config.webexMode) {
    return "webex";
  }
  const electronOrigin = "http://hoylu.local"; //NOSONAR typescript:SS5332 - this http:// is not a call, but a check for the string
  if (window.location.origin.includes(electronOrigin)) {
    return "electron";
  }
  return "";
}

function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function connectFeatureFlagsToDispatch(store: Store) {
  const unsubscribe = store.subscribe(() => {
    const state = store.getState() as RootState;
    if (state.context.config.isLoaded) {
      unsubscribe();
      initLaunchDarkly(store, state.context.config);
    }
  });
}

let ldclient: LDClientInterface;

function initLaunchDarkly(store: Store, config: ConfigState) {
  const user: LDSingleKindContext = {
    kind: "user",
    key: "not_logged_in",
    anonymous: true,
    screen: screenSize(),
    platform: getPlatform(config),
    timezone: getTimezone(),
    appVersion: version,
  };
  const ldClientId = config.isBeta
    ? config.launchDarkly?.betaClientId
    : config.launchDarkly?.clientId;
  ldclient = new LDClientWrapper(ldClientId, user);
  function update() {
    store.dispatch(updateFlags(getFlags()));
  }
  ldclient.on("ready", update);
  ldclient.on("change", update);
}

function getFlags(): FeatureFlags {
  /*
  IMPORTANT!

  If you are adding or removing feature flag please remember to properly update default values for flags
  which are kept in the client-common ( defaultLaunchDarklyFeatureFlag.ts )
   */
  return {
    isAnonymous: !!ldclient.getContext().anonymous,
    enableBetaAccess: !!ldclient.variation("allow-beta-access"),
    enableSupplyChain: !!ldclient.variation("supply-chain-module"),
    enableOrgBanner: !!ldclient.variation("organization-banner"),
    redirectPullPlanningV3ToLegacy: !!ldclient.variation("ppv3-in-legacy"),
    enableUpgradeWithinApp: !!ldclient.variation("upgrade-from-within-app"),
    validateEmailNotification: !!ldclient.variation(
      "validate-email-notification",
    ),
    highlightEnterpriseFeatures: !!ldclient.variation(
      "highlight-enterpise-features",
    ),
    enableHubspot: !!ldclient.variation("enable-hubspot"),
    projectLicensing: !!ldclient.variation("project-licensing"),
    showWorkspaceWeight: !!ldclient.variation("show-workspace-size"),
    workspaceTemplates: !!ldclient.variation("workspace-templates"),
    showLegacyModules: !!ldclient.variation("show-legacy-modules"),
    showTemplateBasedModules: !!ldclient.variation(
      "show-template-based-modules",
    ),
    enableWorkspaceExport: !!ldclient.variation("Workspace-Export"),
    showTemplateCreationUI: !!ldclient.variation("show-template-creation-ui"),
    enableFavouritesCategory: !!ldclient.variation(
      "enable-favourites-category",
    ),
    enableFavoritesWorkspaceSection: !!ldclient.variation(
      "enable-favorites-ws-section",
    ),
    enableInAppProjectCreation: !!ldclient.variation(
      "enable-in-app-project-creation",
    ),
    showCreditLimitUi: !!ldclient.variation("show-credit-limit-ui"),
    orgTemplates: !!ldclient.variation("org-templates"),
    showNotificationsUI: !!ldclient.variation("notifications"),
    enableNexusReporting: !!ldclient.variation("enable-nexus-reporting"),
    showNexusDeleteScope: !!ldclient.variation("show-nexus-delete-scope"),
    showAnnouncementsAutomatically: !!ldclient.variation(
      "show-announcements-automatically",
    ),
    nexusIntegration: ldclient.variation("nexus-live-integration") as unknown as
      | "blocked"
      | "enabled"
      | "only-existing",
    enableZendeskWidget: !!ldclient.variation("enable-zendesk-web-widget"),
    enableNexusInsightsPanel: !!ldclient.variation(
      "enable-nexus-insights-panel",
    ),
    enableInsightsHomeApp: !!ldclient.variation("enable-insights-home-app"),
  };
}

async function updateFeatureFlagUser(user: User, config: ConfigState) {
  // https://docs.launchdarkly.com/sdk/client-side/javascript#users
  const ldUser: LDSingleKindContext = {
    kind: "user",
    key: user.userId,
    email: user.email,
    orgId: user.orgId,
    platform: getPlatform(config),
    screen: screenSize(),
    timezone: getTimezone(),
    appVersion: version,
    _meta: {
      privateAttributes: ["email", "firstName", "lastName"],
    },
  };
  await ldclient.identify(ldUser);

  // in your authentication promise handler or callback

  return getFlags();
}

export const flagsForUserObservable = (user: User, config: ConfigState) =>
  from(updateFeatureFlagUser(user, config));
